import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Upload, Play, ImageIcon } from "lucide-react";
import axios from "axios";
import { formatApiError, API, authHeaders } from "@/hooks/useAdminData";
import { MediaPreviewSmall } from "@/components/MediaPreview";

function MediaCard({ item, onDelete }) {
  const isVideo = item.media_type === "video";

  return (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm group" data-testid={`media-${item.id}`}>
      <div className="relative h-[180px] bg-gray-100">
        <MediaPreviewSmall item={item} />
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              {isVideo ? <Play size={12} className="text-[#C4392D] flex-shrink-0" /> : <ImageIcon size={12} className="text-[#E8922F] flex-shrink-0" />}
              <span className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{item.media_type}</span>
            </div>
            <h4 className="font-semibold text-sm text-[#1A1A1A] truncate">{item.title}</h4>
            {item.description && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{item.description}</p>}
          </div>
          <button onClick={() => onDelete(item.id)} className="text-red-400 hover:text-red-600 p-1.5 flex-shrink-0" data-testid={`delete-media-${item.id}`}>
            <Trash2 size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MediaCollectionManager({ collectionType, title, items, loadData }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", video_url: "", file: null });
  const [uploading, setUploading] = useState(false);

  const filteredItems = items.filter(i => i.collection_type === collectionType);

  const handleAdd = async () => {
    if (!form.file && !form.video_url) {
      alert("Please upload a file or provide a video URL");
      return;
    }
    setUploading(true);
    try {
      const params = new URLSearchParams({
        collection_type: collectionType,
        title: form.title || (form.file ? form.file.name : "Untitled"),
        description: form.description,
        video_url: form.video_url || "",
        media_type: form.video_url && !form.file ? "video" : "image",
      });
      const url = `${API}/collections?${params.toString()}`;

      if (form.file) {
        const formData = new FormData();
        formData.append("file", form.file);
        await axios.post(url, formData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data", ...authHeaders() },
        });
      } else {
        // No file — send empty form data with a dummy file field
        const formData = new FormData();
        formData.append("file", new Blob([]), "");
        await axios.post(url, formData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data", ...authHeaders() },
        });
      }
      setForm({ title: "", description: "", video_url: "", file: null });
      setDialogOpen(false);
      loadData();
    } catch (err) {
      console.error("Upload failed:", err);
      alert(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await axios.delete(`${API}/collections/${id}`, { withCredentials: true, headers: authHeaders() }).catch(() => {});
    loadData();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          {title} ({filteredItems.length})
        </h3>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-secondary text-sm" data-testid={`add-${collectionType}-btn`}><Plus size={14} className="mr-1" /> Add</Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle>Add to {title}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-sm">Title</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="rounded-xl mt-1" placeholder="Enter a title" data-testid={`${collectionType}-title-input`} />
              </div>
              <div>
                <Label className="text-sm font-medium flex items-center gap-2"><Upload size={14} /> Upload Photo or Video</Label>
                <Input
                  type="file"
                  accept="image/*,video/*"
                  onChange={e => setForm({...form, file: e.target.files[0] || null})}
                  className="rounded-xl mt-1"
                  data-testid={`${collectionType}-file-input`}
                />
                {form.file && <p className="text-xs text-green-600 mt-1">Selected: {form.file.name}</p>}
              </div>
              <div className="relative">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-gray-400">OR paste a YouTube/video link</span></div>
              </div>
              <div>
                <Label className="text-sm">Video URL (optional)</Label>
                <Input value={form.video_url} onChange={e => setForm({...form, video_url: e.target.value})} className="rounded-xl mt-1" placeholder="https://youtube.com/watch?v=..." data-testid={`${collectionType}-video-input`} />
              </div>
              <div>
                <Label className="text-sm">Description</Label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-xl mt-1" rows={2} data-testid={`${collectionType}-desc-input`} />
              </div>
              <Button onClick={handleAdd} className="btn-primary w-full" disabled={uploading} data-testid={`${collectionType}-submit-btn`}>
                {uploading ? "Uploading..." : "Add"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {filteredItems.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center">
          <p className="text-gray-400 text-sm">No {title.toLowerCase()} media yet. Click "Add" to upload photos or videos.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map(item => (
            <MediaCard key={item.id} item={item} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </div>
  );
}
